import {
  AccountTree,
  Analytics,
  Build,
  CloudQueue,
  Dashboard,
  Facebook,
  Instagram,
  LinkedIn,
  LocalGasStation,
  LocalPharmacy,
  People,
  PointOfSale,
  Restaurant,
  Rocket,
  Settings,
  Star,
  Store,
  Support,
  TrendingUp,
  Verified,
  Visibility
} from '@mui/icons-material';

/**
 * Single source of truth for site copy and figures.
 *
 * Icons are stored as component references rather than elements so this module
 * stays free of JSX and each section controls its own icon sizing.
 */

export const COMPANY = {
  name: 'Software Systems International (Pvt) Ltd',
  shortName: 'SSIPL',
  tagline: 'Since 2000',
  email: 'info@ssipl.lk',
  phone: '+94 (0) 71 647 4842',
  phoneHref: 'tel:+94716474842',
  location: 'Colombo, Sri Lanka',
  hours: 'Mon to Sun: 7:00 AM to 10:00 PM',
  appUrl: 'https://app.ssipl.lk'
};

export const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'solutions', label: 'Solutions' },
  { id: 'clients', label: 'Clients' },
  { id: 'facts', label: 'Facts' },
  { id: 'contact', label: 'Contact' }
];

export const HERO_STATS = [
  { value: '1,000+', label: 'Active Users' },
  { value: '25+', label: 'Years Experience' },
  { value: '100%', label: 'Sri Lankan' }
];

export const VALUE_PROPS = [
  { icon: Verified, title: 'Quality Assurance', description: 'Rigorous testing and QA ensure reliability.' },
  { icon: Support, title: 'Quick Support', description: 'Responsive support and proactive maintenance.' },
  { icon: TrendingUp, title: 'Continuous Innovation', description: 'Modern tech stacks and rapid iteration.' }
];

export const SOLUTIONS = [
  {
    icon: PointOfSale,
    title: 'POS & Inventory Management',
    description:
      'Comprehensive point-of-sale and inventory management system designed for Sri Lankan retail businesses. Features real-time tracking, barcode integration, and multi-location support.',
    features: [
      'Real-time inventory tracking',
      'Barcode scanning & integration',
      'Multi-location support',
      'Advanced analytics & reporting',
      'Customer management',
      'Supplier integration'
    ],
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#667eea',
    industries: [
      { icon: Store, label: 'Supermarkets', count: '150+' },
      { icon: LocalPharmacy, label: 'Pharmacies', count: '3' },
      { icon: Build, label: 'Hardware', count: '1' },
      { icon: LocalGasStation, label: 'Fuel Stations', count: '3' }
    ]
  },
  {
    icon: People,
    title: 'HR Management System',
    description:
      'Complete human resource management suite with core HR, time & attendance, payroll processing, and employee self-service portal for streamlined workforce management.',
    features: [
      'Employee lifecycle management',
      'Biometric time & attendance',
      'Automated payroll processing',
      'Leave & vacation management',
      'Performance evaluations',
      'Employee self-service portal'
    ],
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    color: '#f5576c',
    industries: []
  },
  {
    icon: Visibility,
    title: 'Visitor Management System',
    description:
      'Advanced visitor monitoring and access control system with digital check-in, real-time alerts, badge printing, and comprehensive security reporting.',
    features: [
      'Digital visitor check-in/out',
      'Photo ID & badge printing',
      'Real-time security alerts',
      'Host notifications',
      'Visitor analytics & reports',
      'Integration with access control'
    ],
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    color: '#4facfe',
    industries: []
  },
  {
    icon: Restaurant,
    title: 'Meal Supply Monitoring',
    description:
      'Smart cafeteria and meal distribution tracking system with proximity card integration, real-time analytics, and comprehensive nutritional reporting.',
    features: [
      'Proximity card integration',
      'Real-time meal tracking',
      'Employee meal preferences',
      'Inventory management',
      'Cost optimization reports'
    ],
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    color: '#fa709a',
    industries: []
  }
];

export const ERP_FEATURES = [
  { icon: Dashboard, title: 'Centralized Dashboard', description: 'Real-time insights and KPIs at a glance' },
  { icon: AccountTree, title: 'Seamless Integration', description: 'All modules work together harmoniously' },
  { icon: CloudQueue, title: 'Cloud-Based', description: 'Access your data anywhere, anytime' },
  { icon: Settings, title: 'Customizable', description: 'Tailored to your specific business needs' }
];

export const CLIENT_CATEGORIES = [
  {
    title: 'Multi-Purpose Cooperative Societies (MPCS)',
    count: '20+ MPCS',
    description: 'Serving over 20 Multi-Purpose Cooperative Societies across Sri Lanka',
    clients: [
      'Biyagama MPCS', 'Dalugama MPCS', 'Dambadeniya MPCS', 'Divulapitiya MPCS',
      'Gampaha CRB', 'Hiriyala MPCS', 'Horana MPCS', 'Horombawa MPCS',
      'Kaduwela MPCS', 'Katugampola MPCS', 'Kehelella MPCS', 'Kelaniya MPCS',
      'Kotadeniyawa MPCS', 'Kuliyapitiya MPCS', 'Kurunegala MPCS', 'Mawathagama MPCS',
      'Minuwangoda MPCS', 'Nattandiya MPCS', 'Negombo MPCS', 'Rideegama MPCS',
      'Wariyapola MPCS', 'Wennappuwa MPCS'
    ]
  },
  {
    title: 'Cooperative Supermarkets',
    count: '190+ Supermarkets',
    description: 'Supporting 190+ cooperative supermarkets across Sri Lanka with resilient POS and inventory solutions.',
    clients: []
  },
  {
    title: 'Corporate Clients',
    count: '35+ Companies',
    description: 'Trusted by leading corporations across various industries',
    clients: [
      'Alliance Development', 'Aluview', 'Arthur C Clarke Center', 'Aruna Tea',
      'Brandix', 'CBC Finance', 'CCS Lanka', 'Ceat', 'Crystal Martin',
      'Dinemore', 'FDK Lanka', 'Finco Limited', 'Gandhara', 'GP Garments',
      'JV Gokal', 'MAS Active', 'MAS Intimates', 'MAS Linea Aqua',
      'MAS Silueta', 'MAS Stretchline', 'Ministry of Science & Technology',
      "Nelu's Advertising Services", 'Nestle Lanka', 'Nugegoda Electricals',
      'Polycrome', 'Rainwear', 'Sampath Center', 'Timee Electric Company',
      'Towerhall Foundation', 'Unity Gold'
    ]
  }
];

export const FACTS = [
  {
    icon: TrendingUp,
    description: 'Of MPCS in Northwestern Province trust our systems',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    count: '33%',
    suffix: 'Market Share'
  },
  {
    icon: Rocket,
    description: 'First to introduce mobile stock-taking app for MPCS',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    count: '1st',
    suffix: 'in Sri Lanka'
  },
  {
    icon: Star,
    description: 'Continuous innovation and reliable service since 2000',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    count: '25+',
    suffix: 'Years'
  }
];

export const SOCIAL_LINKS = [
  { icon: LinkedIn, label: 'LinkedIn', href: 'https://www.linkedin.com/company/ssipl', brand: '#0077b5' },
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com/ssipl.lk', brand: '#1877f2' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com/ssipl.lk', brand: '#E4405F' }
];

export const HERO_ORBIT_ICONS = [Analytics, Dashboard, CloudQueue];
